import React, { useState, KeyboardEvent } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

interface TagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  suggestions?: string[];
  max?: number;
  ariaLabel: string;
}

/**
 * Chip-style multi-value input. Suggestions do most of the work - typing is the
 * fallback, not the primary path, which keeps onboarding fast on mobile.
 */
const TagInput: React.FC<TagInputProps> = ({
  value,
  onChange,
  placeholder,
  suggestions = [],
  max = 10,
  ariaLabel,
}) => {
  const [draft, setDraft] = useState('');

  const add = (raw: string) => {
    const tag = raw.trim().replace(/\s+/g, ' ');
    if (!tag || value.length >= max) return;
    if (value.some((v) => v.toLowerCase() === tag.toLowerCase())) return;
    onChange([...value, tag]);
    setDraft('');
  };

  const remove = (tag: string) => onChange(value.filter((v) => v !== tag));

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      remove(value[value.length - 1]);
    }
  };

  const unusedSuggestions = suggestions.filter(
    (s) => !value.some((v) => v.toLowerCase() === s.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3" aria-live="polite">
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1.5 bg-blue-600 text-white text-sm font-medium pl-3 pr-2 py-1.5 rounded-full"
          >
            {tag}
            <button
              type="button"
              onClick={() => remove(tag)}
              aria-label={`Remove ${tag}`}
              className="hover:bg-blue-700 rounded-full p-0.5 transition-colors"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>

      <input
        type="text"
        value={draft}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => add(draft)}
        disabled={value.length >= max}
        placeholder={value.length >= max ? `Maximum ${max} reached` : placeholder}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition disabled:bg-gray-50 disabled:text-gray-400"
      />

      {unusedSuggestions.length > 0 && value.length < max && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Popular choices
          </p>
          <div className="flex flex-wrap gap-2">
            {unusedSuggestions.slice(0, 12).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => add(s)}
                className="inline-flex items-center gap-1 text-sm text-gray-700 bg-gray-100 hover:bg-blue-50 hover:text-blue-700 px-3 py-1.5 rounded-full transition-colors"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagInput;
